import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import Table from 'app/components/ui/Table';
import s from 'app/components/ui/Table/Table.module.sass';
import TokenName from 'app/components/ui/Table/components/TokenName';
import { AppRoutes } from 'app/static/routes/routes-enum';
import Button from 'app/components/ui/Button';
import BookmarkSvg from 'app/components/common/Bookmark';
import DynamicsWithTooltip from 'app/components/ui/DynamicsWithTooltip';
import { GetTokensQuery } from 'app/generated/graphql';
import { QUIPUSWAP } from 'app/utils/defaults';
import {
  getSlice, getTokenLogo, switchCurrency, transformMutezToTez,
} from 'app/utils/helpers';
import { useCurrency } from 'app/context/CurrencyContext';
import { CurrencyEnum } from 'app/components/ui/CurrencySwitcher/CurrencySwitcher';
import { ReactComponent as TezosSlim } from 'app/components/svg/TezosSlim.svg';

type TokensTableProps = {
  pageSize: number
  data?: GetTokensQuery
  fetch: any
};

const TokensTable: React.FC<TokensTableProps> = ({
  pageSize,
  data,
  fetch,
}) => {
  const { currency, xtzUsdQuote } = useCurrency();

  const [pageCount, setPageCount] = useState<number>(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (data) {
      setPageCount(data.tokens?.totalCount!);
    }
  }, [data]);

  useEffect(() => {
    fetch({
      variables: {
        limit: pageSize ?? 10,
        offset,
        currency,
      },
    });
  }, [currency, fetch, offset, pageSize]);

  const columns = useMemo(() => [
    {
      Header: '#',
      id: 'index',
      accessor: (row: any, index: number) => (
        <div
          className={cx(s.cell)}
        >
          {offset + index + 1}
        </div>
      ),
    },
    {
      Header: 'Name',
      id: 'name',
      accessor: (row: any) => {
        const tokenId: string = `${row.node.standard === 'FA2' ? `_${row.node.tokenId}` : ''}`;

        return (
          <div
            className={cx(s.cell, s.alignLeft)}
          >
            <TokenName
              firstTokenImage={row.node.icon ? getTokenLogo(row.node.icon) : null}
              name={row.node.name ?? (row.node.symbol ? row.node.symbol : 'Unnamed')}
              to={`${AppRoutes.TOKENS}/${row.node.id}${tokenId}`}
              tokensNames={{ firstTokenName: row.node.symbol }}
            />
          </div>
        );
      },
    },
    {
      Header: 'Symbol',
      id: 'symbol',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {
            // eslint-disable-next-line no-nested-ternary
            row.node.symbol
              ? getSlice(row.node.symbol)
              : 'Unnamed'
          }
        </div>
      ),
    },
    {
      Header: 'Liquidity',
      id: 'liquidity',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: (+row.node.liquidity / 2), currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Volume (24h)',
      id: 'volume24h',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {transformMutezToTez({ value: row.node.volume24h, currency, xtzUsdQuote })}
        </div>
      ),
    },
    {
      Header: 'Price',
      id: 'price',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          {currency === CurrencyEnum.USD
            ? '$' : (
              <TezosSlim className={cx(s.tezos)} />
            )}
          {' '}
          {switchCurrency({ value: row.node.price, xtzUsdQuote, currency })}
        </div>
      ),
    },
    {
      Header: 'Price change (24h)',
      id: 'priceChange',
      accessor: (row: any) => (
        <div
          className={cx(s.cell)}
        >
          <DynamicsWithTooltip
            className={cx(s.procent)}
            change={row.node.priceChange}
          />
        </div>
      ),
    },
    {
      Header: 'Trade',
      id: 'trade',
      accessor: (row: any) => {
        const tokenId: string = `${row.node.standard === 'FA2' ? `_${row.node.tokenId}` : ''}`;

        return (
          <div
            className={cx(s.cell)}
          >
            <Button sizeT="small" href={`${QUIPUSWAP}?from=tez&to=${row.node.id}${tokenId}`}>
              Trade
            </Button>
          </div>
        );
      },
    },
    {
      Header: '+',
      id: 'save',
      accessor: (row: any) => {
        const currentItem = data?.tokens?.edges.find((item: any) => `${item.node.id}[${item.node.tokenId}]` === `${row.node.id}[${row.node.tokenId}]`);

        const transformCurrentItem = `${currentItem?.node?.id}[${currentItem?.node?.tokenId}]`;
        return (
          <div
            className={cx(s.cell)}
          >
            <BookmarkSvg
              id={transformCurrentItem}
              field="tokens"
              className={cx(s.bookmark)}
            />
          </div>
        );
      },
    },
  ], [currency, data?.tokens?.edges, offset, xtzUsdQuote]);

  return (
    <Table
      columns={columns}
      data={data?.tokens?.edges ?? []}
      pageCount={pageCount}
      theme="secondary"
      pageSize={pageSize ?? 10}
      setOffset={setOffset}
    />
  );
};

export default TokensTable;
